import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { damga, yazdirBaslikli } from "../lib/stamp";
import PlanDetayModal from "../components/PlanDetayModal";
import QuickPlanModal from "../components/QuickPlanModal";
import { Cog, Printer, Loader2, Download, PlusCircle } from "lucide-react";

const trTarih = (s) => {
  if (!s) return "";
  const p = String(s).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : s;
};

const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function exportExcel(groups, keys) {
  let body = "";
  keys.forEach((k) => {
    body += `<tr><td colspan="7" style="background:#18181B;color:#fff;font-weight:bold;">${esc(k)}</td></tr>`;
    body += `<tr style="background:#E9E9E9;font-weight:bold;"><th>Makina</th><th>Kalıp Kodu</th><th>Ürün Açıklaması</th><th>Şekillenecek (Eksik)</th><th>Şekillendirme Planı</th><th>Termin Tarihleri</th><th>Müşteri</th></tr>`;
    groups[k].forEach((r) => {
      const eksik = Math.abs(Number(r.kalan_stok) || 0);
      const plan = Number(r.sekillendirme_plani) || 0;
      const planTxt = plan ? `${plan}${r.sekillendirme_plani_durum ? " (" + r.sekillendirme_plani_durum + ")" : ""}${r.sekillendirme_plani_makina ? " - " + r.sekillendirme_plani_makina : ""}` : "Plan Yok";
      const term = (r.terminler || []).map((t) => `${trTarih(t.termin)} (${t.kalan})`).join(", ");
      body += `<tr><td>${esc(k)}</td><td>${esc(r.kalip_kodu)}</td><td>${esc(r.urun_aciklamasi)}</td><td>${eksik}</td><td>${esc(planTxt)}</td><td>${esc(term)}</td><td>${esc((r.musteriler || []).join(", "))}</td></tr>`;
    });
  });
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1">${body}</table></body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `Şekillenecek Ürün Makina - ${damga()}.xls`; a.click();
  URL.revokeObjectURL(url);
}

export default function SekillenecekMakina() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detay, setDetay] = useState(null);   // { kalip, aciklama }
  const [quick, setQuick] = useState(null);    // { kalip, aciklama, makina, eksik }
  const [musteri, setMusteri] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/stock/computed")
      .then((r) => setRows((r.data || []).filter((x) => (Number(x.kalan_stok) || 0) < 0)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const musteriList = [...new Set(rows.flatMap((r) => r.musteriler || []))].sort((a, b) => a.localeCompare(b, "tr"));
  const filtered = musteri ? rows.filter((r) => (r.musteriler || []).includes(musteri)) : rows;
  const groups = {};
  filtered.forEach((r) => { const k = r.makina || "Tanımsız Makina"; (groups[k] = groups[k] || []).push(r); });
  Object.values(groups).forEach((list) => list.sort((a, b) => (a.kalan_stok || 0) - (b.kalan_stok || 0)));
  const keys = Object.keys(groups).sort();

  return (
    <>
      <PageHeader title="Şekillenecek Ürün Makina" subtitle="Bisküvi stoğu eksiye düşen (şekillenecek) ürünler — makina bazında"
        actions={
          <div className="flex items-center gap-2">
            <select value={musteri} onChange={(e) => setMusteri(e.target.value)} data-testid="musteri-filtre"
              className="border border-zinc-300 text-sm text-zinc-700 px-3 py-2 rounded-sm bg-white max-w-[260px] no-print">
              <option value="">Tüm Müşteriler</option>
              {musteriList.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {musteri && (
              <button onClick={() => setMusteri("")} data-testid="musteri-filtre-temizle"
                className="text-xs text-zinc-500 hover:text-zinc-800 underline no-print">Temizle</button>
            )}
            <button onClick={() => exportExcel(groups, keys)} data-testid="export-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm">
              <Download size={15} /> Excel'e Aktar
            </button>
            <button onClick={() => yazdirBaslikli(musteri ? `Şekillenecek Ürün Makina - ${musteri}` : "Şekillenecek Ürün Makina")} data-testid="print-btn"
              className="flex items-center gap-2 border border-zinc-300 hover:bg-zinc-50 text-zinc-700 text-sm font-semibold px-3 py-2 rounded-sm">
              <Printer size={15} /> PDF / Yazdır
            </button>
          </div>
        } />
      <div className="p-6 space-y-6 print-area" data-testid="sekillenecek-makina-page">
        {loading ? (
          <div className="text-zinc-400 py-10 text-center"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</div>
        ) : keys.length === 0 ? (
          <div className="text-zinc-400 py-10 text-center border border-zinc-200 rounded-sm">{musteri ? "Bu müşteriye ait şekillenecek ürün yok." : "Eksiye düşen şekillenecek ürün yok."}</div>
        ) : keys.map((k) => {
          const list = groups[k];
          const toplam = list.reduce((s, r) => s + Math.abs(Number(r.kalan_stok) || 0), 0);
          const toplamPlan = list.reduce((s, r) => s + (Number(r.sekillendirme_plani) || 0), 0);
          return (
            <div key={k} className="border border-zinc-200 rounded-sm overflow-hidden" data-testid="makina-grup">
              <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-800 text-white">
                <span className="flex items-center gap-2 font-head font-bold text-sm uppercase tracking-wide">
                  <Cog size={16} /> {k}
                </span>
                <span className="text-xs flex items-center gap-4">
                  <span>Kalem: {list.length}</span>
                  <span>Toplam Şekillenecek: <span className="text-red-300 font-bold">{toplam.toLocaleString("tr-TR")}</span></span>
                  <span>Toplam Kalan Plan: <span className="text-violet-300 font-bold" data-testid={`grup-toplam-plan-${k}`}>{toplamPlan.toLocaleString("tr-TR")}</span></span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="w-40">Kalıp Kodu</th>
                      <th>Ürün Açıklaması</th>
                      <th className="w-44 text-center">Şekillenecek (Eksik)</th>
                      <th className="w-56 text-center">Şekillendirme Planı</th>
                      <th>Termin Tarihleri</th>
                      <th>Müşteri</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r, i) => {
                      const eksik = Math.abs(Number(r.kalan_stok) || 0);
                      const terminler = r.terminler || [];
                      const plan = Number(r.sekillendirme_plani) || 0;
                      const durum = r.sekillendirme_plani_durum;
                      return (
                        <tr key={i} className={i % 2 ? "bg-zinc-50/60" : "bg-white"} data-testid={`makina-row-${r.kalip_kodu}`}>
                          <td className="mono font-semibold text-zinc-900">{r.kalip_kodu}</td>
                          <td className="text-zinc-700">{r.urun_aciklamasi || "—"}</td>
                          <td className="text-center">
                            <span className="inline-block min-w-[64px] bg-red-100 text-red-700 font-bold rounded-md px-2.5 py-1 num">
                              {eksik.toLocaleString("tr-TR")}
                            </span>
                          </td>
                          <td className="text-center">
                            {plan ? (
                              <button type="button" onClick={() => setDetay({ kalip: r.kalip_kodu, aciklama: r.urun_aciklamasi })}
                                data-testid={`plan-ac-${r.kalip_kodu}`}
                                className="inline-flex items-center gap-1.5 justify-center flex-wrap hover:bg-violet-50 rounded-md px-1.5 py-1 transition-colors cursor-pointer">
                                <span className="inline-block min-w-[56px] bg-violet-50 text-violet-700 font-bold rounded-md px-2 py-1 num">{plan.toLocaleString("tr-TR")}</span>
                                {durum && <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold ${durum === "Üretimde" ? "bg-amber-100 text-amber-700 border border-amber-200" : "bg-violet-100 text-violet-700 border border-violet-200"}`}>{durum}</span>}
                                {r.sekillendirme_plani_makina && <span className="w-full text-[11px] font-semibold text-zinc-600" data-testid={`plan-makina-${r.kalip_kodu}`}>{r.sekillendirme_plani_makina}</span>}
                              </button>
                            ) : (
                              <div className="inline-flex items-center gap-2">
                                <span className="inline-block rounded-full bg-red-100 text-red-700 border border-red-200 px-2.5 py-0.5 text-[11px] font-bold" data-testid={`plan-yok-${r.kalip_kodu}`}>Plan Yok</span>
                                <button type="button" onClick={() => setQuick({ kalip: r.kalip_kodu, aciklama: r.urun_aciklamasi, makina: r.makina, eksik })}
                                  data-testid={`plan-olustur-${r.kalip_kodu}`}
                                  className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full transition-colors no-print">
                                  <PlusCircle size={12} /> Plan Oluştur
                                </button>
                              </div>
                            )}
                          </td>
                          <td>
                            {terminler.length === 0 ? <span className="text-zinc-300">—</span> : (
                              <div className="flex flex-wrap gap-1">
                                {terminler.map((t, j) => (
                                  <span key={j} className="inline-flex items-center gap-1 bg-white text-red-700 border border-red-200 rounded-md px-2 py-0.5 text-xs font-semibold whitespace-nowrap shadow-sm">
                                    {trTarih(t.termin)} <span className="text-red-400">({t.kalan})</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="text-zinc-700 text-sm" data-testid={`musteri-${r.kalip_kodu}`}>
                            {(r.musteriler || []).length ? r.musteriler.join(", ") : <span className="text-zinc-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      {detay && <PlanDetayModal kalip={detay.kalip} aciklama={detay.aciklama} onClose={() => setDetay(null)} />}
      {quick && <QuickPlanModal kalip={quick.kalip} aciklama={quick.aciklama} makina={quick.makina} eksik={quick.eksik}
        onClose={() => setQuick(null)} onCreated={load} />}
    </>
  );
}
